import ErrorIcon from '@mui/icons-material/Error'
import {
    Box,
    CircularProgress,
    Divider,
    Skeleton,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material'
import Grid from '@mui/material/Grid2'
import _, { filter, includes, isEmpty, isNull, some } from 'lodash'
import moment from 'moment'
import { useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { DocumentIconName } from '../../../components/DocumentIconName'
import { Reflections } from '../../../components/Reflections'
import VoicePlayer from '../../../components/RequestInput/VoicePlayer'
import { TextCollapse } from '../../../components/TextCollapse/TextCollapse'
import { useTextSelectionMenu } from '../../../hooks/useTextSelectionMenu'
import { useRequestsStore } from '../../../store/storeRequests'
import { ReplyActor, ReplyContentType, ReplyStatus } from '../../../swagger/dto'
import { theme } from '../../../theme'
import { ModelIcon } from '../../InternalPage/MainPage/components/ModelIcon'
import CitationsButton from '../../InternalPage/RequestMessagesPage/components/CitationsButton'
import { DateChip } from '../../InternalPage/RequestMessagesPage/components/DateChip'
import { ImageInRequestList } from '../../InternalPage/RequestMessagesPage/components/ImageInRequestList'
import MarkdownEditor from '../../InternalPage/RequestMessagesPage/components/MarkdownEditor'
import MessageInfoBlock from '../../InternalPage/RequestMessagesPage/components/MessageInfoBlock'
import RequestMessageError from '../../InternalPage/RequestMessagesPage/components/RequestMessageError'
import { MessageWrapper } from '../../InternalPage/RequestMessagesPage/styles'
import { ExternalWrapper } from '../MemoStenographySharePage/components/ExternalWrapper'

const SharedThreadPage = () => {
    const [searchParams] = useSearchParams()
    const id = +searchParams.get('id')
    const {
        requestDetail,
        fetchRequestDetail,
        requestMessages,
        errorRequestDetail,
        fetchSharedThreadMessages,
        isLoadingSharedThreadMessages,
        isLoadingRequestDetail,
    } = useRequestsStore((state) => state)
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    const { handleTextSelectionEnd } = useTextSelectionMenu()

    const messagesEndRef = useRef(null)

    useEffect(() => {
        fetchRequestDetail({ threadId: id }, true)
        fetchSharedThreadMessages({ thread_id: id, limit: 100 }, true)
    }, [])

    let createdAtLast = undefined

    return (
        <ExternalWrapper>
            <Stack direction="row" sx={{ alignItems: 'center' }}>
                <Stack
                    direction="column"
                    sx={{
                        px: 1,
                        alignItems: 'flex-start',
                        display: 'grid',
                    }}>
                    <Typography
                        variant="body2"
                        sx={{
                            color: '#121317',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}>
                        {isLoadingRequestDetail ? (
                            <Skeleton width={210} />
                        ) : (
                            (requestDetail?.name ?? 'Не найден')
                        )}
                    </Typography>
                </Stack>
            </Stack>
            <Divider sx={{ width: '100%', my: isMobile ? 1 : 3, height: ' 1px' }} />
            <Grid
                sx={{
                    overflow: 'auto',
                }}>
                {requestMessages && (
                    <>
                        {_.map(requestMessages, (request, index) => {
                            const {
                                actor,
                                content,
                                created_at,
                                status,
                                id,
                                seconds_to_finish,
                                finished_at,
                                model_slug,
                                actor_name,
                                assistant_id,
                            } = request
                            createdAtLast = index
                                ? requestMessages[index - 1]?.created_at
                                : undefined

                            const isProcessAnswer =
                                status === ReplyStatus.IN_PROCESS && actor === ReplyActor.ASSISTANT

                            const secondsFinish = seconds_to_finish || 0
                            const isActorUser = actor === ReplyActor.USER
                            const imageContent = filter(
                                content,
                                ({ content_type, image_url }) =>
                                    content_type === ReplyContentType.IMAGE && image_url,
                            )
                            const documentContent = filter(
                                content,
                                ({ content_type, file_url }) =>
                                    content_type === ReplyContentType.DOCUMENT && file_url,
                            )
                            const documentReasoning = documentContent?.[0]?.reasoning

                            const isAudio = some(content, ({ content_type }) =>
                                includes(
                                    [ReplyContentType.AUDIO_VOICE, ReplyContentType.AUDIO],
                                    content_type,
                                ),
                            )

                            const isHasTable = () => {
                                const contentText = filter(content, {
                                    content_type: 'TEXT',
                                })
                                return includes(contentText[0]?.text, '|-')
                            }

                            return (
                                <>
                                    <DateChip
                                        createdAt={created_at}
                                        createdAtLast={createdAtLast}
                                    />
                                    <MessageWrapper spacing={0} key={id} container>
                                        <Grid
                                            sx={{
                                                display: 'flex',
                                                opacity: requestDetail?.is_archived ? '0.5' : 1,
                                                width: '100%',
                                                m: 0,
                                                paddingRight: '16px',
                                                justifyContent: isActorUser
                                                    ? 'flex-end'
                                                    : 'flex-start',
                                            }}>
                                            <Stack
                                                sx={{
                                                    overflowX: 'hidden'
                                                }}
                                                direction="row"
                                                spacing={1}
                                                width={isHasTable() ? "clamp(260px, 100%, 100%)" : "clamp(260px, 80%, 1000px)"}
                                                justifyContent={
                                                    isActorUser ? 'flex-end' : 'flex-start'
                                                }>
                                                <Box
                                                    id={`reply_${actor}`}
                                                    sx={{
                                                        overflowX: 'hidden',
                                                        width:
                                                            isProcessAnswer || isAudio
                                                                ? '100%'
                                                                : 'auto',
                                                        display: 'flex',
                                                        gap: '8px',
                                                        flexDirection: 'column',
                                                    }}>
                                                    {!isActorUser ? (
                                                        <Stack
                                                            direction="row"
                                                            sx={{
                                                                alignItems: 'center',
                                                                marginBottom: '4px',
                                                            }}
                                                            spacing={1}>
                                                            <Stack
                                                                spacing={1}
                                                                direction="row"
                                                                sx={{
                                                                    alignItems: 'center',
                                                                    borderRadius: '100px',
                                                                    border: '1px solid  #E7E8EE',
                                                                    padding: '2px 8px 2px 4px',
                                                                }}>
                                                                <Box
                                                                    sx={{
                                                                        textAlign: 'center',
                                                                    }}>
                                                                    <Box
                                                                        sx={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent:
                                                                                'center',
                                                                            background: '#EBF5FD',
                                                                            width: '20px',
                                                                            height: '20px',
                                                                            borderRadius: '50%',
                                                                            position: 'relative',
                                                                        }}>
                                                                        <>
                                                                            <img
                                                                                style={{
                                                                                    width: '16px',
                                                                                }}
                                                                                src="/vector/logo13x13.svg"
                                                                            />
                                                                            {isProcessAnswer && (
                                                                                <CircularProgress
                                                                                    sx={{
                                                                                        position:
                                                                                            'absolute',
                                                                                    }}
                                                                                    size={'20px'}
                                                                                />
                                                                            )}
                                                                        </>
                                                                    </Box>
                                                                </Box>
                                                                <Typography
                                                                    variant="caption2"
                                                                    sx={{
                                                                        color: '#9195A1',
                                                                    }}>
                                                                    {isProcessAnswer
                                                                        ? 'Генерирую ответ'
                                                                        : isNull(assistant_id)
                                                                          ? `Arhiassistant`
                                                                          : actor_name}
                                                                </Typography>
                                                            </Stack>

                                                            {!isProcessAnswer &&
                                                                isNull(assistant_id) &&
                                                                !isNull(actor_name) &&
                                                                model_slug !== 'AUTO' && (
                                                                    <Stack
                                                                        spacing={1}
                                                                        direction="row"
                                                                        sx={{
                                                                            alignItems: 'center',
                                                                            borderRadius: '100px',
                                                                            border: '1px solid  #E7E8EE',
                                                                            padding:
                                                                                '2px 8px 2px 4px',
                                                                        }}>
                                                                        <ModelIcon
                                                                            agentKey={model_slug}
                                                                        />
                                                                        <Typography
                                                                            variant="caption2"
                                                                            sx={{
                                                                                color: '#9195A1',
                                                                            }}>
                                                                            {actor_name}
                                                                        </Typography>
                                                                    </Stack>
                                                                )}

                                                            {!isProcessAnswer && (
                                                                <Typography
                                                                    variant="caption2"
                                                                    sx={{
                                                                        color: '#9195A1',
                                                                        marginLeft: 1,
                                                                    }}>
                                                                    {moment(
                                                                        finished_at ?? created_at,
                                                                    ).format('HH:mm')}
                                                                </Typography>
                                                            )}
                                                        </Stack>
                                                    ) : null}

                                                    <Box
                                                        sx={{
                                                            // display: 'grid',
                                                            gridTemplateColumns: '1fr',
                                                            gap: '16px',
                                                            backgroundColor: isActorUser
                                                                ? '#F4F4F6'
                                                                : '',
                                                            padding: isActorUser ? '8px 12px' : '',
                                                            borderRadius: '20px',
                                                        }}>
                                                        {content.length === 0 && isProcessAnswer ? (
                                                            <Box
                                                                sx={{
                                                                    width: '100%',
                                                                    my: 1,
                                                                }}>
                                                                <Typography
                                                                    variant="caption2"
                                                                    sx={{
                                                                        color: '#9195A1',
                                                                    }}>
                                                                    {secondsFinish > 0
                                                                        ? `Осталось примерно: ${secondsFinish} сек`
                                                                        : 'Еще немного и будет готово...'}
                                                                </Typography>
                                                                <MessageInfoBlock />
                                                            </Box>
                                                        ) : null}

                                                        {content.length === 0 &&
                                                        status === ReplyStatus.FAILED ? (
                                                            <Box
                                                                sx={{
                                                                    width: '100%',
                                                                    my: 1,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                }}>
                                                                <ErrorIcon
                                                                    color="error"
                                                                    sx={{ mr: 1 }}
                                                                />
                                                                Возникла ошибка!
                                                            </Box>
                                                        ) : null}

                                                        {_.map(content, (item) => {
                                                            const { content_type, text } = item
                                                            if (
                                                                content_type ===
                                                                ReplyContentType.TEXT
                                                            ) {
                                                                return (
                                                                    <Box
                                                                        key={`content_type_${id}`}
                                                                        onMouseUp={
                                                                            handleTextSelectionEnd
                                                                        }
                                                                        onTouchEnd={
                                                                            handleTextSelectionEnd
                                                                        }
                                                                        sx={{
                                                                            position: 'relative',
                                                                            wordBreak: 'break-all',
                                                                            gridColumn: '1 / -1',
                                                                        }}>
                                                                        {item.reasoning && (
                                                                            <Reflections
                                                                                reasoning={
                                                                                    item.reasoning
                                                                                }
                                                                            />
                                                                        )}
                                                                        <CitationsButton
                                                                            citations={
                                                                                item.citations
                                                                            }
                                                                        />
                                                                        <MarkdownEditor
                                                                            citations={
                                                                                item.citations
                                                                            }
                                                                            text={text.replaceAll(
                                                                                '---\n',
                                                                                '',
                                                                            )}
                                                                        />
                                                                    </Box>
                                                                )
                                                            }

                                                            if (
                                                                content_type ===
                                                                    ReplyContentType.AUDIO_VOICE &&
                                                                item?.voice_url
                                                            ) {
                                                                return (
                                                                    <Box key={`content_type_${id}`}>
                                                                        <CitationsButton
                                                                            citations={
                                                                                item.citations
                                                                            }
                                                                        />
                                                                        <VoicePlayer
                                                                            url={item.voice_url}
                                                                            autoplay={
                                                                                actor ===
                                                                                    ReplyActor.ASSISTANT &&
                                                                                requestMessages.at(
                                                                                    0,
                                                                                )?.id === id &&
                                                                                secondsFinish > -30
                                                                            }
                                                                        />

                                                                        {item.stenography && (
                                                                            <>
                                                                                <Typography
                                                                                    component="p"
                                                                                    sx={{
                                                                                        marginTop:
                                                                                            '16px',
                                                                                        marginBottom:
                                                                                            '12px',
                                                                                    }}
                                                                                    color="#9195A1"
                                                                                    variant="caption2">
                                                                                    Расшифровка
                                                                                    сообщения
                                                                                </Typography>
                                                                                <Grid
                                                                                    onMouseUp={
                                                                                        handleTextSelectionEnd
                                                                                    }
                                                                                    onTouchEnd={
                                                                                        handleTextSelectionEnd
                                                                                    }
                                                                                    sx={{
                                                                                        border: '1px solid #E7E8EE',
                                                                                        borderRadius:
                                                                                            '16px',
                                                                                        padding:
                                                                                            '16px',
                                                                                        width: '100%',
                                                                                    }}>
                                                                                    <TextCollapse
                                                                                        maxLines={2}
                                                                                        text={
                                                                                            <MarkdownEditor
                                                                                                citations={
                                                                                                    item.citations
                                                                                                }
                                                                                                text={
                                                                                                    item.stenography
                                                                                                }
                                                                                            />
                                                                                        }
                                                                                    />
                                                                                </Grid>
                                                                            </>
                                                                        )}
                                                                    </Box>
                                                                )
                                                            }

                                                            if (
                                                                content_type ===
                                                                    ReplyContentType.AUDIO &&
                                                                item?.audio_url
                                                            ) {
                                                                return (
                                                                    <VoicePlayer
                                                                        key={`content_type_${id}`}
                                                                        url={item.audio_url}
                                                                        downloadable
                                                                    />
                                                                )
                                                            }

                                                            if (
                                                                content_type ===
                                                                    ReplyContentType.VIDEO &&
                                                                item?.video_url
                                                            ) {
                                                                return (
                                                                    <video
                                                                        key={`content_type_${id}`}
                                                                        style={{
                                                                            borderRadius: '16px',
                                                                        }}
                                                                        width={
                                                                            isMobile ? '100%' : 560
                                                                        }
                                                                        controls>
                                                                        <source
                                                                            src={item?.video_url}
                                                                            type="video/mp4"
                                                                        />
                                                                    </video>
                                                                )
                                                            }
                                                            if (
                                                                content_type ===
                                                                ReplyContentType.ERROR
                                                            ) {
                                                                return (
                                                                    <RequestMessageError
                                                                        key={`content_type_${id}`}
                                                                        text={text}
                                                                    />
                                                                )
                                                            }
                                                        })}
                                                        {isActorUser && (
                                                            <Typography
                                                                variant="caption2"
                                                                sx={{
                                                                    color: '#9195A1',
                                                                    marginLeft: 'auto',
                                                                }}>
                                                                {moment(created_at).format('HH:mm')}
                                                            </Typography>
                                                        )}
                                                    </Box>

                                                    {!isEmpty(imageContent) ? (
                                                        <ImageInRequestList
                                                            actor={actor}
                                                            imageContent={imageContent}
                                                        />
                                                    ) : null}

                                                    {!isEmpty(documentContent) ? (
                                                        <Box
                                                            sx={{
                                                                position: 'relative',
                                                                wordBreak: 'break-all',
                                                                gridColumn: '1 / -1',
                                                            }}>
                                                          {(documentReasoning) && (
                                                                <Reflections
                                                                    reasoning={documentReasoning}
                                                                />
                                                            )}
                                                            <Box
                                                                sx={{
                                                                    width: '100%',
                                                                    display: 'flex',
                                                                    gap: '16px',
                                                                    flexWrap: 'wrap',
                                                                    [theme.breakpoints.down('sm')]: {
                                                                        flexWrap: 'nowrap',
                                                                        flexDirection: 'column',
                                                                    },
                                                                }}>
                                                                {_.map(
                                                                    documentContent,
                                                                    ({ file_url }) => {
                                                                        const name = file_url.substring(
                                                                            file_url.lastIndexOf('/') +
                                                                                1,
                                                                        )
                                                                        return (
                                                                            <DocumentIconName
                                                                                key={file_url}
                                                                                url={file_url}
                                                                                name={name}
                                                                                isShared={true}
                                                                            />
                                                                        )
                                                                    },
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    ) : null}
                                                </Box>
                                            </Stack>
                                        </Grid>
                                    </MessageWrapper>
                                </>
                            )
                        })}

                        <span ref={messagesEndRef} />
                    </>
                )}

                {isLoadingSharedThreadMessages && (
                    <>
                        <Skeleton
                            variant="circular"
                            width="160px"
                            height={24}
                            sx={{ margin: 'auto', borderRadius: '24px' }}
                        />
                        <Skeleton
                            variant="rectangular"
                            width="100%"
                            height={104}
                            sx={{ borderRadius: '24px' }}
                        />
                        <Skeleton
                            variant="circular"
                            width="160px"
                            height={24}
                            sx={{ margin: 'auto', borderRadius: '24px' }}
                        />
                        <Skeleton
                            variant="rectangular"
                            width="100%"
                            height={104}
                            sx={{ borderRadius: '24px' }}
                        />
                        <Skeleton
                            variant="circular"
                            width="160px"
                            height={24}
                            sx={{ margin: 'auto', borderRadius: '24px' }}
                        />
                        <Skeleton
                            variant="rectangular"
                            width="100%"
                            height={104}
                            sx={{ borderRadius: '24px' }}
                        />
                    </>
                )}
            </Grid>

            {errorRequestDetail && (
                <Typography color="error" variant="h4" sx={{ margin: '40px auto ' }}>
                    Пользователь удалил этот диалог, но вы можете создать свой, войдя или
                    зарегистрировавшись в продукте
                </Typography>
            )}
        </ExternalWrapper>
    )
}

export default SharedThreadPage
